import React from "react";
import { BuildingOUT, LicenseFeatureEnum } from "api-client";
import { useLocation, useNavigate } from "react-router";
import { ROUTES_ENUM } from "routes/consts";
import { buildingStore } from "store/Building/BuildingStore";
import { URBAN_MINING_HUB_URL, localStorageKeys } from "utils";
import { searchStore } from "features/ProductSearch/SearchStore";
import { IconFile, IconLayoutDashboard } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { IconHomes, IconProductManagement, IconBricks } from "utils";
import { useFeatureAccess } from "hooks/useFeatureAccess";
import { sidebarMenuStore } from "./SidebarMenuStore";

const useSidebarMenu = (organisationID?: string) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { hasFeature } = useFeatureAccess();

  const openLastEditedSharedBuilding = () => {
    const savedBuildingId = localStorage.getItem(
      localStorageKeys.SHARED_LAST_CLICKED_BUILDING
    );
    if (savedBuildingId) {
      const savedBuilding = buildingStore.sharedBuildings.items.find(
        (item) => item.building.id === savedBuildingId
      );
      if (savedBuilding) {
        buildingStore.setCurrentBuilding(savedBuilding.building);
        const buildingURL = savedBuilding.building.license
          ? ROUTES_ENUM.BUILDINGS
          : ROUTES_ENUM.BUILDING_NO_LICENSE;
        navigate(`/${buildingURL}/${savedBuildingId}`);
      } else {
        navigateToFirstSharedBuilding();
      }
    } else {
      navigateToFirstSharedBuilding();
    }
  };

  const navigateToFirstSharedBuilding = () => {
    const firstBuilding = buildingStore.sharedBuildings.items[0].building;
    buildingStore.setCurrentBuilding(firstBuilding);
    const buildingURL = firstBuilding.license
      ? ROUTES_ENUM.BUILDINGS
      : ROUTES_ENUM.BUILDING_NO_LICENSE;
    navigate(`/${buildingURL}/${firstBuilding.id}`);
  };

  const saveLastEditedSharedBuilding = (building: BuildingOUT) => {
    localStorage.setItem(
      localStorageKeys.SHARED_LAST_CLICKED_BUILDING,
      building.id
    );
  };

  function openManufacturerProductList() {
    searchStore.setOpenEPDSearch({ state: false, term: "" });
    searchStore.resetForOrganisationSearch();
    navigate(`../${organisationID}/${ROUTES_ENUM.ORGANIZATION_PRODUCT_LIST}`);
  }

  function openManufacturerEPDList() {
    navigate(`../${organisationID}/${ROUTES_ENUM.ORGANIZATION_EPD_LIST}`);
  }

  function openUrbanMiningHub() {
    navigate(`../${organisationID}/${URBAN_MINING_HUB_URL}`);
  }

  function openCircularAssetPortfolio() {
    navigate(`../${organisationID}/${ROUTES_ENUM.PORTFOLIO_ASSETS}`);
  }

  function openManufacturerDashboard() {
    navigate(`../${organisationID}/${ROUTES_ENUM.MANUFACTURER_DASHBOARD}`);
  }

  function openLCADashboard() {
    buildingStore.setBuildingID(undefined);
    navigate(`../${ROUTES_ENUM.DASHBOARD}`);
  }
  function openPortfolio() {
    buildingStore.setBuildingID(undefined);
    navigate(`../${organisationID}/${ROUTES_ENUM.PORTFOLIO_SCREENER}`);
  }

  const EPDListMenuItem = {
    visible: true,
    icon: <IconFile />,
    onClick: () => openManufacturerEPDList(),
    label: t("dashboard.EPDList"),
    selected: pathname.includes(ROUTES_ENUM.ORGANIZATION_EPD_LIST),
  };

  const ProductListMenuItem = {
    visible: true,
    icon: IconProductManagement,
    onClick: () => openManufacturerProductList(),
    label: t("dashboard.productList"),
    selected: pathname.includes(ROUTES_ENUM.ORGANIZATION_PRODUCT_LIST),
  };

  const portfolioScreenerMenuItem = {
    visible: hasFeature(LicenseFeatureEnum.PotfolioScreener),
    icon: IconHomes,
    onClick: () => openPortfolio(),
    label: "Portfolio Screener",
    selected: pathname.includes(ROUTES_ENUM.PORTFOLIO_SCREENER),
  };

  const lcaDashboardMenuItem = {
    visible: true,
    icon: <IconLayoutDashboard />,
    onClick: () => openLCADashboard(),
    label: "Dashboard",
    selected: pathname.includes(ROUTES_ENUM.DASHBOARD),
  };

  const manufacturerDashboardMenuItem = {
    visible: true,
    icon: IconHomes,
    onClick: () => openManufacturerDashboard(),
    label: "Dashboard",
    selected: pathname.includes(ROUTES_ENUM.MANUFACTURER_DASHBOARD),
  };

  const urbanMiningMenuItem = {
    visible: hasFeature(LicenseFeatureEnum.MaterialSalesFlow),
    icon: IconBricks,
    onClick: () => openUrbanMiningHub(),
    label: "Urban Mining",
    selected: pathname.includes(URBAN_MINING_HUB_URL),
  };

  const portfolioAssetMenuItem = {
    visible: sidebarMenuStore.hasOrganisationAssetLicense(),
    icon: IconHomes,
    onClick: () => openCircularAssetPortfolio(),
    label: "Portfolio Assets",
    selected: pathname.includes(ROUTES_ENUM.PORTFOLIO_ASSETS),
  };

  return {
    openLastEditedSharedBuilding,
    saveLastEditedSharedBuilding,
    EPDListMenuItem,
    lcaDashboardMenuItem,
    portfolioScreenerMenuItem,
    ProductListMenuItem,
    manufacturerDashboardMenuItem,
    urbanMiningMenuItem,
    portfolioAssetMenuItem,
  };
};

export default useSidebarMenu;
